/**
 * The data vocabulary — the components a government console is actually built
 * from, rather than the generic ones a component library ships with.
 *
 * ============================================================================
 * PORTED FROM THE HOUSE STYLE (samkad.uz), MEASURED RATHER THAN EYEBALLED.
 * ============================================================================
 *
 * The owner named that console as the target, so its geometry was read out of
 * the running page instead of guessed at: an 11 px uppercase header at 0.22 px
 * tracking on navy with a 2 px gold rule under it, 12 px rows on a #b8bec9
 * grid line, 16 px card radius, one 0 4px 16px/8% elevation. Those numbers are
 * in `tailwind.config.cjs` as tokens; this file spends them.
 *
 * ============================================================================
 * DENSITY IS THE POINT, AND DENSITY IS NOT DECORATION.
 * ============================================================================
 *
 * Every component here exists because an operator asks a question that a card
 * with one number cannot answer. A rank row carries a name, a workload, a
 * progress bar, a headline figure, a fortnight of shape and two counts because
 * "which mahalla is in trouble" is not answerable from any one of them. The
 * test for adding anything to this file is that it replaces a question the
 * operator would otherwise have to open a second screen to ask — not that it
 * fills space.
 *
 * The chart rules from `charts.tsx` hold here too: status colours mean status,
 * series wear brand and gold, and identity is never colour alone.
 */
import type { ReactNode } from 'react';
import { t } from '../i18n/index.js';

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

export interface Column<Row> {
  readonly key: string;
  readonly label: string;
  /** Right-align numerics. Words left, numbers right — always. */
  readonly numeric?: boolean;
  readonly render: (row: Row) => ReactNode;
  readonly width?: string;
}

/**
 * The signature table: navy header, gold rule, banded rows, optional total.
 *
 * A NATIVE `<table>`, not a grid of divs. Screen readers announce a real table
 * by row and column, an operator can select a column of figures and paste it
 * into a spreadsheet, and browser find lands on the row it matched. Those are
 * the three things people actually do with a table of government figures, and
 * a div grid breaks all of them to gain a layout convenience nobody asked for.
 */
export function DataTable<Row>({
  columns,
  rows,
  keyOf,
  total,
  onRowClick,
  rowAccent,
}: {
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly keyOf: (row: Row) => string;
  /** The bold summary line. Omitted when a total would be meaningless. */
  readonly total?: ReactNode[];
  readonly onRowClick?: (row: Row) => void;
  /**
   * A Tailwind BACKGROUND class for the row's left edge — `bg-crit`, `bg-ok`.
   *
   * Worth having: on a grid of thirty-five machines the status is one pill
   * among eleven columns, and finding the two broken ones means reading down a
   * column. A colour bar at the start of the row turns that into a glance
   * without adding a twelfth column.
   *
   * ======================================================================
   * A BACKGROUND CLASS ON ITS OWN ELEMENT, NOT A BORDER ON THE CELL.
   * ======================================================================
   *
   * The first cut returned `border-crit` and put it on the `<td>` beside
   * `border-grid`. Both are `border-color` at the same specificity, so which
   * one won came down to their order in the generated stylesheet — and it went
   * DIFFERENT WAYS for different colours. The critical row, the one this whole
   * device exists for, drew its accent in the table's hairline grey while the
   * offline rows drew theirs correctly. A background on a separate span cannot
   * collide with anything.
   *
   * Returns a class, not a tone, because the caller already knows the machine's
   * status and translating it twice is how two screens end up disagreeing.
   */
  readonly rowAccent?: (row: Row) => string | null;
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={`border-b-2 border-gold bg-brand px-2 py-1.5 text-grid-head font-bold uppercase text-white ${
                  column.numeric ? 'text-right' : 'text-left'
                }`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={keyOf(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              /* Banding by row rather than by hover alone: on twelve columns of
                 numbers the eye loses its line halfway across, and a hover
                 highlight only helps the row the mouse is already on. */
              className={`${index % 2 === 1 ? 'bg-panel-raised' : ''} ${
                onRowClick ? 'cursor-pointer hover:bg-panel-raised' : ''
              }`}
            >
              {columns.map((column, columnIndex) => {
                /* The accent rides on the FIRST cell rather than on the <tr>:
                   a border on a table row is not painted at all under
                   `border-collapse`, which is what this table uses to get its
                   hairlines. */
                const accent = columnIndex === 0 ? (rowAccent?.(row) ?? null) : null;
                return (
                  <td
                    key={column.key}
                    className={`border-b border-grid px-2 py-1 text-grid text-ink-100 ${
                      column.numeric ? 'text-right tabular-nums' : 'text-left'
                    } ${accent ? 'relative pl-3' : ''}`}
                  >
                    {accent ? (
                      <span
                        className={`absolute inset-y-0 left-0 w-1 ${accent}`}
                        aria-hidden="true"
                      />
                    ) : null}
                    {column.render(row)}
                  </td>
                );
              })}
            </tr>
          ))}
          {total ? (
            <tr className="bg-panel-raised font-semibold">
              {total.map((cell, index) => (
                <td
                  key={columns[index]?.key ?? String(index)}
                  className={`border-t-2 border-brand px-2 py-1.5 text-grid text-ink-100 ${
                    columns[index]?.numeric ? 'text-right tabular-nums' : 'text-left'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small parts
// ---------------------------------------------------------------------------

/*
  `offline` IS ITS OWN TONE, and adding it fixed a real misreading.

  It used to be folded into `neutral`, and `neutral` resolves to `text-ink-100`
  — the darkest ink in the theme. So the status pipeline drew the offline
  kiosks as a SOLID BLACK bar, next to green, amber and red ones. Black is the
  loudest thing on a white page: three offline kiosks read as the most urgent
  fact on the console, when offline is the calmest of the five states.

  §15.2 already has a colour for it (a slate that the donut and the map were
  both using) — the tone map simply had no name for it, so the two halves of
  the same chart disagreed. Now they cannot: there is one union and every map
  keyed by it must answer for the case.
*/
export type Tone = 'ok' | 'warn' | 'crit' | 'info' | 'neutral' | 'offline';

const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
  info: 'text-info',
  offline: 'text-offline',
  neutral: 'text-ink-300',
};

/**
 * The Tailwind text class for a tone — for the places that need a class name
 * rather than a component (SVG fills, chart slices).
 *
 * Exported so no screen keeps its OWN status→class map. One did, and it is how
 * the pipeline and the donut ended up drawing the same offline kiosks in two
 * different colours for a day.
 */
export function toneClass(tone: Tone): string {
  return TONE_TEXT[tone];
}

const TONE_CHIP: Record<Tone, string> = {
  ok: 'border-ok/30 bg-ok/10 text-ok',
  warn: 'border-warn/30 bg-warn/10 text-warn',
  crit: 'border-crit/30 bg-crit/10 text-crit',
  info: 'border-info/30 bg-info/10 text-info',
  offline: 'border-offline/30 bg-offline/10 text-offline',
  neutral: 'border-hairline bg-panel-raised text-ink-300',
};

/**
 * A count with its meaning attached — «Yakunlangan · 138».
 *
 * The label travels with the number because a bare 138 in a green box is a
 * number an operator has to remember the meaning of, and they are reading
 * thirty-five rows.
 */
export function StatChip({
  tone = 'neutral',
  label,
  value,
}: {
  readonly tone?: Tone;
  readonly label: string;
  readonly value?: string | number;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-micro ${TONE_CHIP[tone]}`}
    >
      <span>{label}</span>
      {value === undefined ? null : <span className="tabular-nums font-semibold">{value}</span>}
    </span>
  );
}

/**
 * A change against the previous period, with its direction in the glyph.
 *
 * The arrow is what carries the meaning; the colour agrees with it. A reader
 * with no colour vision loses nothing — which is the rule everywhere in this
 * panel, and matters most on the figures somebody acts on.
 *
 * `invert` is for measures where down is good (overdue appeals, queue depth).
 * Without it a falling backlog would be painted as a failure.
 */
export function TrendDelta({
  value,
  invert = false,
  suffix = '',
}: {
  readonly value: number | null;
  readonly invert?: boolean;
  readonly suffix?: string;
}): React.JSX.Element | null {
  if (value === null || Number.isNaN(value)) return null;
  if (value === 0) {
    return <span className="text-micro tabular-nums text-ink-500">—</span>;
  }
  const up = value > 0;
  const good = invert ? !up : up;
  return (
    <span className={`text-micro tabular-nums ${good ? 'text-ok' : 'text-crit'}`}>
      {up ? '▲' : '▼'} {Math.abs(value).toFixed(1)}
      {suffix}
    </span>
  );
}

/** A bar with its target marked. The target is the whole point of the bar. */
export function ProgressBar({
  pct,
  target = null,
  tone = 'ok',
}: {
  readonly pct: number;
  readonly target?: number | null;
  readonly tone?: Tone;
}): React.JSX.Element {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-hairline">
      <div
        className={`h-full rounded-full ${TONE_TEXT[tone]}`}
        style={{ width: `${clamped}%`, backgroundColor: 'currentColor' }}
      />
      {target === null ? null : (
        /* A hairline at the target, so "is this row above or below the line" is
           answered by looking rather than by reading the number and comparing. */
        <div
          className="absolute inset-y-0 w-px bg-ink-100/50"
          style={{ left: `${Math.max(0, Math.min(100, target))}%` }}
        />
      )}
    </div>
  );
}

/** The live pip. One animation in this app, reserved for things that are live. */
export function LiveBadge({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-ok">
      <span className="h-1.5 w-1.5 animate-live rounded-full bg-ok" />
      {label}
    </span>
  );
}

/**
 * Rank 1–3 get a medal; the rest get their number.
 *
 * Not decoration: a leaderboard whose top rows look like every other row is a
 * leaderboard nobody reads as one. Past third place the number is more useful
 * than a fourth medal colour nobody can rank at a glance.
 */
export function RankBadge({ rank }: { readonly rank: number }): React.JSX.Element {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
  if (medal) {
    return (
      <span className="text-lead" aria-label={t('rank.position', { n: String(rank) })}>
        {medal}
      </span>
    );
  }
  return <span className="text-micro tabular-nums text-ink-500">#{rank}</span>;
}

// ---------------------------------------------------------------------------
// Composites
// ---------------------------------------------------------------------------

/**
 * A filter row that shows what it is filtering by.
 *
 * A dropdown hides the options and, worse, hides which one is active behind a
 * click. On a console read across a room the current sort has to be legible
 * from the doorway.
 */
export function SegmentedControl<Value extends string>({
  options,
  value,
  onChange,
}: {
  readonly options: readonly { value: Value; label: string }[];
  readonly value: Value;
  readonly onChange: (value: Value) => void;
}): React.JSX.Element {
  return (
    <div className="inline-flex rounded border border-hairline bg-panel-raised p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={option.value === value}
          className={`rounded px-2.5 py-1 text-micro font-semibold transition-colors duration-state ${
            option.value === value
              ? 'bg-panel text-ink-100 shadow-card'
              : 'text-ink-500 hover:text-ink-300'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The dark banner: who is watching, what is live, what needs attention.
 *
 * It is the one place in this panel that is deliberately loud, and it earns it
 * by carrying the exceptions. §15.7 asks the executive home to answer "what is
 * wrong today" before anything else, and the alert chips are that answer at the
 * top of the page rather than three scrolls into it.
 */
export function HeroBanner({
  eyebrow,
  title,
  subtitle,
  actions,
  alerts,
}: {
  readonly eyebrow: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly actions?: ReactNode;
  readonly alerts?: readonly { key: string; tone: Tone; label: string }[];
}): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-card border border-brand-deep bg-brand-deep bg-hero shadow-lift">
      <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5">
        <div className="min-w-0">
          <div className="flex items-center gap-3">{eyebrow}</div>
          <h1 className="mt-2 text-hero font-bold leading-none text-white">{title}</h1>
          <p className="mt-2 text-label text-white/70">{subtitle}</p>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {alerts && alerts.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-t border-white/10 px-6 py-3">
          {alerts.map((alert) => (
            <span
              key={alert.key}
              /* On navy the status colours are used as TEXT on a tinted panel
                 rather than as fills — a solid #dc2626 block on #0c1d3a is a
                 smear, and the same red as ink reads cleanly. */
              className={`inline-flex items-center gap-2 rounded border px-2.5 py-1 text-micro font-medium ${
                alert.tone === 'crit'
                  ? 'border-crit/40 bg-crit/15 text-crit'
                  : alert.tone === 'warn'
                    ? 'border-warn/40 bg-warn/15 text-warn'
                    : 'border-info/40 bg-info/15 text-info'
              }`}
            >
              {alert.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One line of a live event stream: when, what, who.
 *
 * The three facts are fixed and in that order because the question a feed
 * answers is always "what just changed, and who do I ask about it". A feed
 * without the actor is a feed that generates a second question.
 */
export function FeedItem({
  tone = 'neutral',
  when,
  title,
  actor,
}: {
  readonly tone?: Tone;
  readonly when: string;
  readonly title: string;
  readonly actor?: string;
}): React.JSX.Element {
  return (
    <li className="flex gap-2.5 border-b border-hairline py-2 last:border-0">
      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${TONE_TEXT[tone]}`} style={{ backgroundColor: 'currentColor' }} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-label text-ink-100">{title}</p>
        <p className="text-micro text-ink-500">
          <span className="tabular-nums">{when}</span>
          {actor ? ` · ${actor}` : ''}
        </p>
      </div>
    </li>
  );
}

/**
 * A KPI with its trend and its comparison, in one tile.
 *
 * The number alone is the thing every dashboard shows and the thing nobody can
 * act on: 1,721 appeals is neither good nor bad until you know it was 1,600
 * yesterday. The delta is therefore not optional decoration on this tile — it
 * is half the content.
 */
export function KpiTile({
  label,
  value,
  absent,
  delta,
  deltaLabel,
  tone = 'neutral',
  invertDelta = false,
  footer,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly absent: string;
  readonly delta?: number | null;
  readonly deltaLabel?: string;
  readonly tone?: Tone;
  readonly invertDelta?: boolean;
  readonly footer?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-card border border-hairline bg-panel bg-card px-4 py-3 shadow-card transition-shadow duration-state hover:shadow-lift">
      <p className="text-micro uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`mt-1 text-figure font-bold tabular-nums leading-none ${TONE_TEXT[tone]}`}>
        {value ?? <span className="text-ink-500">{absent}</span>}
      </p>
      {delta === undefined ? null : (
        <p className="mt-1.5 flex items-center gap-1.5">
          <TrendDelta value={delta} invert={invertDelta} />
          {deltaLabel ? <span className="text-micro text-ink-500">{deltaLabel}</span> : null}
        </p>
      )}
      {footer ? <div className="mt-2">{footer}</div> : null}
    </div>
  );
}
